import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Trash2,
  Smartphone,
  Mail,
  CheckCircle2,
  Clock,
  ShieldCheck,
  AlertCircle,
} from "lucide-react";
import Seo, { breadcrumbLd, organizationLd } from "../components/Seo";
import { site } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/;

const SCOPES = [
  {
    id: "everything",
    title: "Delete my account and everything in it",
    text: "Account, email address, password, profile, scan and barcode history, saved products, shopping list, watchlist and payment records.",
  },
  {
    id: "scans",
    title: "Delete only my scan and barcode history",
    text: "Keeps your account and settings, removes every scan, photo-read label, question and result.",
  },
  {
    id: "emails",
    title: "Remove my email from launch and mailing lists",
    text: "Keeps your app account, stops all launch announcements and digests.",
  },
];

const DataDeletion = () => {
  const [form, setForm] = useState({
    email: "",
    account_email: "",
    scope: "everything",
    platform: "ios",
    details: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!EMAIL_RE.test(form.email.trim())) {
      setError("Please enter a valid email address so we can confirm the deletion.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/data-deletion`, form);
      setDone(r.data);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Could not send the request. Please email support@inzorah.com instead."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo
        title="Delete your account and data | InZoRAH"
        description="Request deletion of your InZoRAH account, scan and barcode history, or email address. What gets deleted, what is kept, how long it takes, and how to do it inside the app."
        path="/data-deletion"
        keywords="delete InZoRAH account, delete my data, data deletion request, remove scan history"
        jsonLd={[
          organizationLd,
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Delete your data", path: "/data-deletion" },
          ]),
        ]}
      />
      <div className="blog-shell">
        <header className="blog-top">
          <Link to="/" className="blog-brand">
            InZoRAH
          </Link>
          <nav aria-label="Site navigation">
            <Link to="/ingredients">Ingredients A-Z</Link>
            <Link to="/legal">Legal</Link>
          </nav>
        </header>

        <main className="blog-main blog-article">
          <p className="blog-eyebrow">Your data</p>
          <h1 className="post-h1">Delete your account and data</h1>
          <p className="blog-intro">
            You can delete your InZoRAH data yourself inside the app at any time, or ask us
            to do it here — including if you no longer have the app installed. We action
            every request within 30 days and email you when it is done.
          </p>

          <div className="dd-cards">
            <section className="dd-card">
              <span className="dd-ico">
                <Smartphone size={17} aria-hidden="true" />
              </span>
              <h2>Delete it yourself in the app</h2>
              <p>
                Open InZoRAH and go to <strong>Profile → Delete my account</strong>. This
                removes your account and its data immediately and cannot be undone. To clear
                only your history, use <strong>Profile → Scan history → Clear all</strong>.
              </p>
              <p className="dd-note">
                If you subscribed through the App Store or Google Play, also cancel the
                subscription in your store account — deleting the app or your InZoRAH
                account does not cancel store billing.
              </p>
            </section>

            <section className="dd-card">
              <span className="dd-ico">
                <Mail size={17} aria-hidden="true" />
              </span>
              <h2>Ask us to delete it</h2>
              <p>
                Use the form below, or email{" "}
                <a href={`mailto:${site.email}`}>{site.email}</a> from the address on your
                account with the subject "Delete my data". We may ask you to confirm the
                request came from you before we delete anything.
              </p>
              <p className="dd-note">
                No account needed — this works even if you only ever gave us your email
                address for launch news.
              </p>
            </section>
          </div>

          <h2 className="dd-h2">What gets deleted</h2>
          <ul className="dd-list">
            <li>
              <CheckCircle2 size={14} aria-hidden="true" /> Your account, name, email
              address and password hash
            </li>
            <li>
              <CheckCircle2 size={14} aria-hidden="true" /> Your full scan and barcode
              history, including label photos read, questions asked and results
            </li>
            <li>
              <CheckCircle2 size={14} aria-hidden="true" /> Your profile: diet, allergies,
              skin concerns, avoided ingredients and profile photo
            </li>
            <li>
              <CheckCircle2 size={14} aria-hidden="true" /> Saved products, shopping list,
              watchlist and household circle membership
            </li>
            <li>
              <CheckCircle2 size={14} aria-hidden="true" /> Sessions, push tokens, referral
              records and your email on any mailing list
            </li>
          </ul>

          <h2 className="dd-h2">What we keep, and for how long</h2>
          <ul className="dd-list dd-list-muted">
            <li>
              <Clock size={14} aria-hidden="true" /> Encrypted backups roll off within 30
              days of deletion.
            </li>
            <li>
              <Clock size={14} aria-hidden="true" /> Payment and tax records are kept only
              as long as the law requires, and are held by our payment processor.
            </li>
            <li>
              <Clock size={14} aria-hidden="true" /> Anonymous, aggregated usage statistics
              that can no longer be linked to you may remain.
            </li>
            <li>
              <Clock size={14} aria-hidden="true" /> Product information you contributed to
              the shared product database stays, but it is not linked to your identity.
            </li>
          </ul>

          <h2 className="dd-h2">Request deletion</h2>
          {done ? (
            <div className="dd-done">
              <ShieldCheck size={20} aria-hidden="true" />
              <div>
                <b>Request received</b>
                <p>{done.message}</p>
                <p className="dd-ticket">
                  Reference: <code>{done.ticket}</code> — keep this if you need to follow up.
                </p>
              </div>
            </div>
          ) : (
            <form className="dd-form" onSubmit={submit} noValidate>
              <fieldset className="dd-scopes">
                <legend>What should we delete?</legend>
                {SCOPES.map((s) => (
                  <label
                    key={s.id}
                    className={`dd-scope ${form.scope === s.id ? "is-on" : ""}`}
                  >
                    <input
                      type="radio"
                      name="scope"
                      value={s.id}
                      checked={form.scope === s.id}
                      onChange={set("scope")}
                    />
                    <span>
                      <b>{s.title}</b>
                      <em>{s.text}</em>
                    </span>
                  </label>
                ))}
              </fieldset>

              <div className="dd-row">
                <label className="dd-field">
                  <span>Your email address *</span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@email.com"
                    autoComplete="email"
                    required
                  />
                  <em>We use this only to confirm the deletion, then delete it too.</em>
                </label>
                <label className="dd-field">
                  <span>Account email, if different</span>
                  <input
                    type="email"
                    value={form.account_email}
                    onChange={set("account_email")}
                    placeholder="Optional"
                    autoComplete="off"
                  />
                  <em>Helpful if you signed up with another address.</em>
                </label>
              </div>

              <label className="dd-field">
                <span>Where did you use InZoRAH?</span>
                <select value={form.platform} onChange={set("platform")}>
                  <option value="ios">iPhone / iPad (App Store)</option>
                  <option value="android">Android (Google Play)</option>
                  <option value="web">Website only</option>
                  <option value="none">Not sure</option>
                </select>
              </label>

              <label className="dd-field">
                <span>Anything else we should know?</span>
                <textarea
                  rows="3"
                  value={form.details}
                  onChange={set("details")}
                  placeholder="Optional — for example a second account, or a specific scan to remove."
                />
              </label>

              {error && (
                <p className="dd-error">
                  <AlertCircle size={14} aria-hidden="true" /> {error}
                </p>
              )}

              <div className="dd-actions">
                <button
                  type="submit"
                  className="dd-submit"
                  disabled={busy}
                  data-testid="deletion-submit-button"
                >
                  <Trash2 size={16} aria-hidden="true" />
                  {busy ? "Sending…" : "Send deletion request"}
                </button>
              </div>
              <p className="dd-fine">
                Submitting this form only creates a deletion request — it does not create an
                account. See our <Link to="/legal/privacy">Privacy Policy</Link> for the full
                detail on what we store and why.
              </p>
            </form>
          )}
        </main>

        <footer className="blog-foot">
          KYSolutions LLC · <Link to="/contact">Contact</Link> ·{" "}
          <a
            className="inz-support-link"
            href="https://royalelite.support"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="support-portal-link"
          >
            Support
          </a>{" "}
          ·{" "}
          
          <Link to="/legal/privacy">Privacy</Link> ·{" "}
          <Link to="/legal/terms">Terms</Link> · <Link to="/">Home</Link> ·{" "}
          <Link to="/admin/login" rel="nofollow">
            Admin
          </Link>
        </footer>
      </div>
    </>
  );
};

export default DataDeletion;
